import { useTranslation } from '@/app/i18n/client';
import AppContext from '@/components/AppContext';
import AddCircle from '@mui/icons-material/AddCircle';
import RemoveCircle from '@mui/icons-material/RemoveCircle';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import ListItem from '@mui/material/ListItem';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import { useContext } from 'react';
import {
  Control,
  Controller,
  UseFieldArrayAppend,
  UseFieldArrayRemove,
  UseFieldArrayUpdate,
} from 'react-hook-form';
import { InferType, object, string } from 'yup';
import { RootInputs } from './Form';

export const peopleSchema = object({
  firstName: string().required('Imię jest wymagane'),
  lastName: string(),
  position: string(),
});

type PeopleInputs = InferType<typeof peopleSchema>;
type PeopleFieldNames = keyof Required<PeopleInputs>;
type PeopleFieldSpec = any; // FieldSpec<PeopleFieldNames>;
type PeopleFields = {
  [key in PeopleFieldNames]: PeopleFieldSpec;
};

interface PeopleEntryProps {
  allowNew?: boolean;
  maxEntries: number;
  control: Control<RootInputs, any, any>;
  append: UseFieldArrayAppend<RootInputs, 'people'>;
  update: UseFieldArrayUpdate<RootInputs, 'people'>;
  remove: UseFieldArrayRemove;
  index: number;
  lastItem?: boolean;
  value: any;
}

export default function PeopleEntry({
  allowNew = false,
  maxEntries,
  control,
  index,
  append,
  remove,
  update,
  lastItem = false,
  value: { id, ...defaultValues },
}: PeopleEntryProps) {
  const app = useContext(AppContext);
  const { t } = useTranslation(app.i18n.lng);
  // const {
  //   register,
  //   handleSubmit,
  //   formState: { errors },
  // } = useForm({
  //   defaultValues,
  // });

  const spec: PeopleFields = {
    firstName: {
      name: `people.${index}.firstName`,
      label: 'Imię',
    },
    lastName: {
      name: `people.${index}.lastName`,
      label: 'Nazwisko',
    },
    position: {
      name: `people.${index}.position`,
      label: 'Stanowisko',
    },
  };

  return (
    <ListItem
      secondaryAction={
        index === 0 && !allowNew ? (
          <Tooltip
            title={t('Maximum number of entries is: {{max}}', {
              max: maxEntries,
            })}
          >
            <span>
              <IconButton size="small" disabled>
                <AddCircle />
              </IconButton>
            </span>
          </Tooltip>
        ) : (
          <IconButton
            size="small"
            onClick={() => {
              index === 0 ? append(defaultValues) : remove(index);
            }}
          >
            {index === 0 ? <AddCircle /> : <RemoveCircle />}
          </IconButton>
        )
      }
      sx={{
        mb: lastItem ? 0 : 4,
      }}
    >
      <Grid
        container
        spacing={2}
        sx={{
          flexGrow: 1,
          mr: 2,
        }}
      >
        <Grid size={{ xs: 12, md: 4 }}>
          <Controller
            name={spec.firstName.name}
            control={control}
            defaultValue=""
            render={({ field, fieldState: { error } }) => (
              <FormControl fullWidth required error={!!error}>
                <TextField
                  id={`${spec.firstName.name}`}
                  error={!!error}
                  label={spec.firstName.label}
                  required
                  {...field}
                  helperText={error?.message}
                />
              </FormControl>
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Controller
            name={spec.lastName.name}
            control={control}
            defaultValue=""
            render={({ field, fieldState: { error } }) => (
              <FormControl fullWidth error={!!error}>
                <TextField
                  id={`${spec.lastName.name}`}
                  error={!!error}
                  label={spec.lastName.label}
                  {...field}
                  helperText={error?.message}
                />
              </FormControl>
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Controller
            name={spec.position.name}
            control={control}
            defaultValue=""
            render={({ field, fieldState: { error } }) => (
              <FormControl fullWidth error={!!error}>
                <TextField
                  id={`${spec.position.name}`}
                  error={!!error}
                  label={spec.position.label}
                  {...field}
                  helperText={error?.message}
                />
              </FormControl>
            )}
          />
        </Grid>
      </Grid>
    </ListItem>
  );
}
